'use client';

import React, { FC, useEffect, useState, useRef } from 'react';
import { Heading } from '@/lib/blog/blog-utils';
import { motion } from 'framer-motion';

export default function TableOfContents({ headings }: { headings: Heading[] }) {
  const [activeId, setActiveId] = useState<string>('');
  const visibleHeadings = useRef<Record<string, boolean>>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visibleHeadings.current[entry.target.id] = entry.isIntersecting;
        });

        const visibleIds = Object.keys(visibleHeadings.current).filter(
          (id) => visibleHeadings.current[id]
        );

        if (visibleIds.length > 0) {
          setActiveId(visibleIds[0]);
        }
      },
      { 
        rootMargin: '-80px 0% -75% 0%', 
        threshold: 0 
      }
    );

    headings.forEach((h) => {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [headings]);

  const handleScrollTo = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; 
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
      window.history.pushState(null, '', `#${id}`);
    }
  };

  if (headings.length === 0) return null;

  return (
    <nav className="relative py-2 ml-4">
      {/* BACKGROUND LINE */}
      <div className="absolute left-[-1px] top-0 bottom-0 w-[1px] bg-zinc-100 dark:bg-zinc-800/50" />
      
      <h4 className="pl-6 text-[10px] font-black uppercase tracking-[0.4em] mb-6 text-zinc-400 dark:text-zinc-500 flex items-center gap-2">
        <span className="w-1 h-1 bg-indigo-500 rounded-full" />
        Table of Contents
      </h4>

      <ul className="space-y-1">
        {headings.map((h) => {
          const isActive = activeId === h.id;
          return (
            <li 
              key={h.id} 
              className="relative group"
              style={{ paddingLeft: h.level === 3 ? '2.5rem' : '1.5rem' }}
            >
              {/* ACTIVE INDICATOR */}
              {isActive && (
                <motion.div 
                  layoutId="activeIndicator"
                  className="absolute left-[-1px] w-[2px] h-full bg-indigo-500 z-10"
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              )}

              <a
                href={`#${h.id}`}
                onClick={(e) => handleScrollTo(e, h.id)}
                className={`py-2 text-[12px] leading-snug transition-all block ${
                  isActive 
                    ? 'text-indigo-600 dark:text-indigo-400 font-bold' 
                    : 'text-zinc-500 dark:text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200 font-medium'
                }`}
              >
                {/* RESTORED: Removed .replace() and font-mono / uppercase classes */}
                {h.text}
              </a>
            </li>
          );
        })}
      </ul>

      {/* FOOTER STATS */}
      <div className="mt-8 pl-6 border-t border-zinc-50 dark:border-zinc-900/50 pt-4">
        <p className="text-[9px] font-medium text-zinc-400 dark:text-zinc-600">
          {headings.length} Sections available
        </p>
      </div>
    </nav>
  );
}